/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import {
  Box,
  Button,
  Divider,
  Icon,
  PricingSelector,
  type PricingSelectorOption,
  Text,
} from '@landtrustinc/design-system';
import React, { useMemo } from 'react';

import type { LtPlusBillingInterval, LtPlusCta, StripeConfig } from '../../../types';
import type { AnalyticsHandler } from '../../SignupDrawer/hooks/useSignupFlow';
import { useBenefitsStep } from '../hooks/useBenefitsStep';
import {
  formatLtPlusCurrency,
  formatLtPlusPlanPrice,
  getAvailableLtPlusBillingIntervals,
  getLtPlusPlanAmount,
} from '../ltPlusPlans';
import LtPlusBenefitsList from './LtPlusBenefitsList';
import LtPlusStepLayout from './LtPlusStepLayout';

type BenefitsStepProps = {
  ltPlusCta: LtPlusCta;
  stripeConfig: StripeConfig;
  showSignInButton: boolean;
  selectedBillingInterval: LtPlusBillingInterval;
  onBillingIntervalChange: (billingInterval: LtPlusBillingInterval) => void;
  onCheckoutClick: () => void;
  onSignInClick: () => void;
  onAnalyticsEvent?: AnalyticsHandler;
};

const pricingSelectorStyles = css`
  .buck-chat-pricing-selector button[role='radio']:focus-within {
    outline: none;
    box-shadow: none;
  }
`;

const BenefitsStep: React.FC<BenefitsStepProps> = ({
  ltPlusCta,
  stripeConfig,
  showSignInButton,
  selectedBillingInterval,
  onBillingIntervalChange,
  onCheckoutClick,
  onSignInClick,
  onAnalyticsEvent,
}) => {
  const {
    content,
    showPromoBox,
    promoTitle,
    promoDescription,
    promoDescriptionExtra,
    benefitsHelperText,
    handleCheckoutClick,
    handleSignInClick,
  } = useBenefitsStep({
    ltPlusCta,
    showSignInButton,
    billingInterval: selectedBillingInterval,
    onCheckoutClick,
    onSignInClick,
    onAnalyticsEvent,
  });

  const availableBillingIntervals = useMemo(
    () => getAvailableLtPlusBillingIntervals(stripeConfig),
    [stripeConfig]
  );

  const annualAmount = getLtPlusPlanAmount(stripeConfig, 'annual');
  const monthlyAmount = getLtPlusPlanAmount(stripeConfig, 'monthly');
  const selectedAmount = getLtPlusPlanAmount(stripeConfig, selectedBillingInterval);
  const annualMonthlyEquivalent = annualAmount / 12;

  const planOptions = useMemo<PricingSelectorOption[]>(
    () =>
      availableBillingIntervals.map((billingInterval) => {
        if (billingInterval === 'annual') {
          return {
            value: 'annual',
            title: 'Annual',
            badge: 'Best Value',
            supportingText: `Just ${formatLtPlusCurrency(annualMonthlyEquivalent, 2)}/mo • Save over $60`,
            price: formatLtPlusPlanPrice(annualAmount, 'annual'),
          };
        }

        return {
          value: 'monthly',
          title: 'Monthly',
          supportingText: 'Cancel anytime',
          price: formatLtPlusPlanPrice(monthlyAmount, 'monthly'),
        };
      }),
    [annualAmount, annualMonthlyEquivalent, availableBillingIntervals, monthlyAmount]
  );

  const continueLabel = useMemo(() => {
    if (!selectedAmount) {
      return content.ctaText;
    }

    return `Continue for ${formatLtPlusPlanPrice(selectedAmount, selectedBillingInterval)}`;
  }, [content.ctaText, selectedAmount, selectedBillingInterval]);

  return (
    <LtPlusStepLayout title={content.headline}>
      {showPromoBox && (
        <Box
          display="flex"
          flexDirection="column"
          gap="var(--spacing-1)"
          p="var(--spacing-4)"
          bg="var(--surface-neutral)"
          borderRadius="var(--radius-md)"
        >
          <Box display="flex" alignItems="center" gap="var(--spacing-2)">
            <Icon
              variant="ShieldCheckSolid"
              fill="var(--icon-warning)"
              size="medium"
              css={css`
                flex-shrink: 0;
              `}
            />

            <Text fontWeight="extrabold">{promoTitle}</Text>
          </Box>
          <Box>
            <Text size="sm" color="var(--color-gray-600)" textWrap="wrap">
              {promoDescription} {promoDescriptionExtra && <i>{promoDescriptionExtra}</i>}
            </Text>
          </Box>
        </Box>
      )}

      <Box width="100%" css={pricingSelectorStyles}>
        <Box mb="var(--spacing-3)">
          <Text size="sm" color="var(--text-primary)">
            Choose Your Plan
          </Text>
        </Box>
        <PricingSelector
          className="buck-chat-pricing-selector"
          value={selectedBillingInterval}
          options={planOptions}
          onChange={(nextValue: string) =>
            onBillingIntervalChange(nextValue as LtPlusBillingInterval)
          }
          layout="vertical"
          aria-label="Choose billing interval"
        />
      </Box>

      <Divider />

      <LtPlusBenefitsList
        benefits={content.benefits}
        helperText={showPromoBox ? benefitsHelperText : undefined}
      />

      <Box display="flex" flexDirection="column" gap="var(--spacing-3)" width="100%">
        <Button variant="plus" fullWidth onClick={handleCheckoutClick}>
          {continueLabel}
        </Button>
        {showSignInButton && (
          <Button variant="secondary" fullWidth onClick={handleSignInClick}>
            Already a LandTrust+ Member? Sign In
          </Button>
        )}
      </Box>
    </LtPlusStepLayout>
  );
};

export default BenefitsStep;
